import type { StateCreator } from 'zustand/vanilla';
import {
	GridFilterModel,
	GridLogicOperator,
	TablePinnedColumns,
	TableVisibility,
} from '@warehouse/object-browser/core';
import isEqual from 'lodash/isEqual';
import { PaginatorStore } from '../paginator/paginator.store';
import { gridFilterModelToFilterQuery } from '../table/muix.adapter';
import { components } from '../../../object-browser/infra/inventory.openapi';

type DefaultSortField = components['schemas']['NxObjectSearchableFields'];

export interface TableColumnSort<F extends string = DefaultSortField> {
	field: F;
	sort: 'asc' | 'desc' | null | undefined;
}

export interface TableManagerStore<F extends string = DefaultSortField> {
	sort: TableColumnSort<F>[];
	filter: GridFilterModel;
	visibility: TableVisibility;
	pinned: TablePinnedColumns;
	order: string[];
	actions: {
		setSort: (sortModel: TableColumnSort<F>[]) => void;
		setVisibility: (visibilityModel: TableVisibility) => void;
		setPinned: (pinnedModel: TablePinnedColumns) => void;
		setOrder: (order: string[]) => void;
		setFilter: (filter: GridFilterModel) => void;
	};
}

interface FactoryOptions {
	getPaginatorStore: () => PaginatorStore;
}

export const createTableManagerStoreSliceFactory =
	<F extends string = DefaultSortField>({ getPaginatorStore }: FactoryOptions): StateCreator<TableManagerStore<F>> =>
	(set, get) => ({
		sort: [],
		filter: {
			items: [],
			logicOperator: GridLogicOperator.AND,
		},
		visibility: {},
		pinned: {
			left: [],
			right: [],
		},
		order: [],
		actions: {
			setSort: (value: TableColumnSort<F>[]) => {
				set({ sort: value });
				getPaginatorStore().internalActions.resetPagination();
			},
			setVisibility: (value: TableVisibility) => set({ visibility: value }),
			setPinned: (pinnedModel: TablePinnedColumns) => set({ pinned: pinnedModel }),
			setOrder: (order: string[]) => set({ order }),
			setFilter: (filter: GridFilterModel) => {
				if (!isEqual(gridFilterModelToFilterQuery(filter), gridFilterModelToFilterQuery(get().filter))) {
					getPaginatorStore().internalActions.resetPagination();
				}
				set({ filter });
			},
		},
	});

export const tableManagerStoreSelector = {
	filter: <F extends string>(state: TableManagerStore<F>) => state.filter,
	sort: <F extends string>(state: TableManagerStore<F>) => state.sort,
	visibility: <F extends string>(state: TableManagerStore<F>) => state.visibility,
	pinned: <F extends string>(state: TableManagerStore<F>) => state.pinned,
	order: <F extends string>(state: TableManagerStore<F>) => state.order,
	actions: <F extends string>(state: TableManagerStore<F>) => state.actions,
};
